import React, {ReactNode} from 'react'
import EventEmitterEx from '../../pageBaseComponents/EventEmitterEx'
import {RequestItemError} from '../../../../network/response/RequestItemError'
import {MessagePair} from '../FormErrorMessage'

interface ExternalProps {
    eventEmitter?: EventEmitterEx
    eventKeys?: string[]
    onChange?: (event: any, checked?: boolean) => void
    clearError?: boolean
}

export interface ErrorDisplayProps {
    hasError?: boolean
    clearError?: boolean
    clearErrorMessage?: () => void
    eventEmitter?: EventEmitterEx
    eventKeys?: string[]
    errorAreaFullSize?: boolean
    errorAreaMaxWidth?: number
    messageInvisible?: boolean
}

export const withErrorSubscribe = <OriginalProps extends {}>(
    WrappedComponent: React.ComponentType<OriginalProps & ErrorDisplayProps>,
) => {

    type ResultProps = OriginalProps & ExternalProps

    interface State {
        messages: MessagePair[]
        hasError: boolean
    }

    return class extends React.Component<ResultProps, State> {

        static displayName: string = `withErrorSubscribe(${WrappedComponent.displayName})`

        constructor(props: Readonly<OriginalProps & ExternalProps>) {
            super(props)
            this.state = {
                messages: [],
                hasError: false,
            }

            this.handleEmitMessage = this.handleEmitMessage.bind(this)
            this.handdleClearErrorMessage = this.handdleClearErrorMessage.bind(this)
        }

        handleEmitMessage(itemError: RequestItemError): void {
            this.setState((prevState) => {
                return {
                    messages: [...prevState.messages, {message: itemError.message}],
                    hasError: true,
                }
            })
        }

        componentDidUpdate(prevProps: Readonly<ResultProps>): void {
            if ((!prevProps.clearError) && this.props.clearError) {
                this.handdleClearErrorMessage()
            }
            const prevEventKeys = prevProps.eventKeys
            const {eventKeys, eventEmitter} = this.props

            if (!prevEventKeys) {
                return
            }
            if (!eventKeys) {
                return
            }
            if (!eventEmitter) {
                return
            }
            if (JSON.stringify(prevEventKeys) !== JSON.stringify(eventKeys)) {
                prevEventKeys.forEach(eventKey => {
                    eventEmitter.off(eventKey, this.handleEmitMessage)
                })
                eventKeys.forEach(eventKey => {
                    eventEmitter.on(eventKey, this.handleEmitMessage)
                })
            }
        }

        componentDidMount(): void {
            const {eventKeys, eventEmitter} = this.props

            if (!eventKeys) {
                return
            }
            if (!eventEmitter) {
                return
            }
            eventEmitter.on('clearAllErrors', this.handdleClearErrorMessage)
            eventKeys.forEach(eventKey => {
                eventEmitter.on(eventKey, this.handleEmitMessage)
                eventEmitter.on(`clear:${eventKey}`, this.handdleClearErrorMessage)
            })
        }

        componentWillUnmount(): void {
            const {eventKeys, eventEmitter} = this.props
            if (!eventKeys) {
                return
            }
            if (!eventEmitter) {
                return
            }
            eventEmitter.off('clearAllErrors', this.handdleClearErrorMessage)
            eventKeys.forEach(eventKey => {
                eventEmitter.off(eventKey, this.handleEmitMessage)
                eventEmitter.off(`clear:${eventKey}`, this.handdleClearErrorMessage)
            })
        }

        handdleClearErrorMessage(): void {
            this.setState({
                messages: [],
                hasError: false,
            })
        }

        handleOnChange(event: any): void {
            this.handdleClearErrorMessage()
            if (this.props.onChange) {
                this.props.onChange(event)
            }
        }

        render(): ReactNode {
            return (
                <WrappedComponent
                    {...this.props}
                    {...this.state}
                    onChange={this.handleOnChange.bind(this)}
                    clearErrorMessage={this.handdleClearErrorMessage.bind(this)}
                />
            )
        }
    }
}
